<template>
  <div>
    <!-- Header and Search -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4>{{ $t('admin.users.title') }}</h4>
      <div class="d-flex">
        <input
          type="text"
          v-model="searchQuery"
          @input="handleSearch"
          class="form-control mr-2"
          :placeholder="$t('admin.users.searchPlaceholder')"
        />
        <button class="btn btn-primary" @click="showAddModal">
          <i class="ti-plus"></i> {{ $t('admin.users.addUser') }}
        </button>
      </div>
    </div>

    <!-- Users Table -->
    <div class="card">
      <div class="card-body">
        <div class="table-responsive">
          <table class="table">
            <thead>
            <tr>
              <th @click="sort('_id')">
                {{ $t('admin.users.id') }}
                <i class="ti-arrow-up" v-if="sortBy === '_id' && !sortDesc" :title="$t('admin.users.sortAscending')"></i>
                <i class="ti-arrow-down" v-if="sortBy === '_id' && sortDesc" :title="$t('admin.users.sortDescending')"></i>
              </th>
              <th @click="sort('name')">
                {{ $t('admin.users.name') }}
                <i class="ti-arrow-up" v-if="sortBy === 'name' && !sortDesc" :title="$t('admin.users.sortAscending')"></i>
                <i class="ti-arrow-down" v-if="sortBy === 'name' && sortDesc" :title="$t('admin.users.sortDescending')"></i>
              </th>
              <th @click="sort('mail')">
                {{ $t('admin.users.email') }}
                <i class="ti-arrow-up" v-if="sortBy === 'mail' && !sortDesc" :title="$t('admin.users.sortAscending')"></i>
                <i class="ti-arrow-down" v-if="sortBy === 'mail' && sortDesc" :title="$t('admin.users.sortDescending')"></i>
              </th>
              <th>{{ $t('admin.users.userUrl') }}</th>
              <th @click="sort('last_login')">
                {{ $t('admin.users.lastLogin') }}
                <i class="ti-arrow-up" v-if="sortBy === 'last_login' && !sortDesc" :title="$t('admin.users.sortAscending')"></i>
                <i class="ti-arrow-down" v-if="sortBy === 'last_login' && sortDesc" :title="$t('admin.users.sortDescending')"></i>
              </th>
              <th @click="sort('package_id')">
                {{ $t('admin.users.package') }}
                <i class="ti-arrow-up" v-if="sortBy === 'package_id' && !sortDesc" :title="$t('admin.users.sortAscending')"></i>
                <i class="ti-arrow-down" v-if="sortBy === 'package_id' && sortDesc" :title="$t('admin.users.sortDescending')"></i>
              </th>
              <th>{{ $t('admin.users.actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="user in users" :key="user._id">
              <td>{{ user._id }}</td>
              <td>{{ user.name }} {{ user.surname }}</td>
              <td>{{ user.mail }}</td>
              <td>
                <div class="d-flex align-items-center">
                    <span class="text-truncate" style="max-width: 200px;" :title="getUserUrl(user.userHash)">
                      {{ getUserUrl(user.userHash) }}
                    </span>
                  <button
                    class="btn btn-sm btn-outline-primary ml-2"
                    @click="copyUserUrl(user.userHash)"
                    :title="$t('admin.users.copyUrl')"
                  >
                    <i class="ti-clipboard"></i>
                  </button>
                </div>
              </td>
              <td>
                {{ user.last_login ?  formatLastLogin(user.last_login) : '-' }}
              </td>
              <td>{{ user.package_id }}</td>
              <td>
                <button class="btn btn-info btn-sm mr-2" @click="editUser(user)" :title="$t('admin.users.editUser')">
                  <i class="ti-pencil"></i>
                </button>
                <button class="btn btn-danger btn-sm" @click="deleteUser(user)" :title="$t('admin.users.delete')">
                  <i class="ti-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center mt-4">
          <div>
            {{ $t('admin.users.showing') }} {{ (page - 1) * limit + 1 }} - {{ Math.min(page * limit, totalUsers) }}
            {{ $t('admin.users.of') }} {{ totalUsers }}
          </div>
          <div>
            <button
              class="btn btn-sm btn-primary mr-2"
              @click="changePage(page - 1)"
              :disabled="page === 1"
              :title="$t('admin.users.previousPage')"
            >
              <i class="ti-angle-left"></i>
            </button>
            <button
              class="btn btn-sm btn-primary"
              @click="changePage(page + 1)"
              :disabled="page * limit >= totalUsers"
              :title="$t('admin.users.nextPage')"
            >
              <i class="ti-angle-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add User Modal -->
    <div class="modal-wrapper" :class="{ 'show': showAddUserModal }">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('admin.users.addUser') }}</h5>
            <button type="button" class="close" @click="closeAddUserModal" :title="$t('admin.users.close')">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="createUser">
              <div class="form-group">
                <label>{{ $t('admin.users.name') }}</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="newUser.name"
                  required
                  :placeholder="$t('admin.users.name')"
                >
                <div class="invalid-feedback">{{ $t('admin.users.required.name') }}</div>
              </div>
              <div class="form-group">
                <label>{{ $t('admin.users.surname') }}</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="newUser.surname"
                  required
                  :placeholder="$t('admin.users.surname')"
                >
                <div class="invalid-feedback">{{ $t('admin.users.required.surname') }}</div>
              </div>
              <div class="form-group">
                <label>{{ $t('admin.users.email') }}</label>
                <input
                  type="email"
                  class="form-control"
                  v-model="newUser.mail"
                  required
                  :placeholder="$t('admin.users.email')"
                >
                <div class="invalid-feedback">{{ $t('admin.users.invalid.email') }}</div>
              </div>
              <div class="form-group">
                <label>{{ $t('admin.users.password') }}</label>
                <input
                  type="password"
                  class="form-control"
                  v-model="newUser.password"
                  required
                >
                <div class="invalid-feedback">{{ $t('admin.users.required.password') }}</div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeAddUserModal">
                  {{ $t('admin.users.cancel') }}
                </button>
                <button type="submit" class="btn btn-primary" :disabled="isLoading">
                  {{ isLoading ? $t('admin.users.saving') : $t('admin.users.save') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit User Modal -->
    <div class="modal-wrapper" :class="{ 'show': showEditModal }">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('admin.users.editUser') }}</h5>
            <button type="button" class="close" @click="closeEditModal" :title="$t('admin.users.close')">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="updateUser">
              <div class="form-group">
                <label>{{ $t('admin.users.name') }}</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="editingUser.name"
                  required
                  :placeholder="$t('admin.users.name')"
                >
                <div class="invalid-feedback">{{ $t('admin.users.required.name') }}</div>
              </div>
              <div class="form-group">
                <label>{{ $t('admin.users.surname') }}</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="editingUser.surname"
                  required
                  :placeholder="$t('admin.users.surname')"
                >
                <div class="invalid-feedback">{{ $t('admin.users.required.surname') }}</div>
              </div>
              <div class="form-group">
                <label>{{ $t('admin.users.email') }}</label>
                <input
                  type="email"
                  class="form-control"
                  v-model="editingUser.mail"
                  required
                  :placeholder="$t('admin.users.email')"
                >
                <div class="invalid-feedback">{{ $t('admin.users.invalid.email') }}</div>
              </div>
              <div class="form-group">
                <label>{{ $t('admin.users.password') }}</label>
                <input
                  type="password"
                  class="form-control"
                  v-model="editingUser.password"
                  :placeholder="$t('admin.users.newPasswordPlaceholder')"
                >
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeEditModal">
                  {{ $t('admin.users.cancel') }}
                </button>
                <button type="submit" class="btn btn-primary" :disabled="isLoading">
                  {{ isLoading ? $t('admin.users.updating') : $t('admin.users.update') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal-wrapper" :class="{ 'show': showDeleteModal }">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('admin.users.deleteConfirmTitle') }}</h5>
            <button type="button" class="close" @click="closeDeleteModal" :title="$t('admin.users.close')">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p>{{ $t('admin.users.deleteConfirmMessage') }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDeleteModal">
              {{ $t('admin.users.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="confirmDelete"
              :disabled="isLoading"
            >
              {{ isLoading ? $t('admin.users.deleting') : $t('admin.users.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Modal Overlay -->
    <div class="modal-overlay" :class="{ 'show': showAddUserModal || showEditModal || showDeleteModal }"></div>
  </div>
</template>

<script>
import axios from "axios";
import config from "@/routing_map.json";
import NotificationTemplate from "@/pages/Notifications/NotificationTemplate.vue";

export default {
  name: "adminUsers",
  data() {
    return {
      users: [],
      page: 1,
      limit: 10,
      totalUsers: 0,
      searchQuery: '',
      sortBy: '_id',
      sortDesc: false,
      searchTimeout: null,
      showAddUserModal: false,
      showDeleteModal: false,
      isLoading: false,
      userToDelete: null,
      newUser: {
        name: '',
        surname: '',
        mail: '',
        password: ''
      },
      showEditModal: false,
      editingUser: {
        _id: null,
        name: '',
        surname: '',
        mail: '',
        password: ''
      },
      basePreviewUrl: 'https://preview.onlinecardvisit.com/qr-user/?hash=',
    }
  },
  created() {
    this.getUsers();
    this.closeAllModals()
  },

  methods: {
    getUsers() {
      axios.defaults.headers.common.authorization = localStorage.getItem("authorization");

      axios.get(`${config.app_url}/user/list-users`, {
        params: {
          page: this.page,
          limit: this.limit,
          search: this.searchQuery,
          sortDesc: this.sortDesc // Sadece sortDesc gönderiyoruz
        }
      })
        .then(response => {
          if (response.data.success) {
            this.users = response.data.msg.data;
            this.totalUsers = response.data.msg.total;
          } else {
            this.notifyVue("top", "right", this.$t('admin.users.fetchError'), "warning");
          }
        })
        .catch(() => {
          this.$router.push("login");
          this.notifyVue("top", "right", this.$t('admin.users.connectionError'), "warning");
        });
    },

    changePage(newPage) {
      if (newPage >= 1 && newPage * this.limit <= this.totalUsers + this.limit) {
        this.page = newPage;
        this.getUsers();
      }
    },

    handleSearch() {
      // Debounce arama işlemi
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.page = 1; // Aramada ilk sayfaya dön
        this.getUsers();
      }, 500);
    },

    sort(column) {
      // Artık sadece sıralama yönünü değiştiriyoruz
      this.sortDesc = !this.sortDesc;
      this.getUsers();
    },
    deleteUser(user) {
      this.closeAllModals();
      this.userToDelete = user;
      this.showDeleteModal = true;
    },

    notifyVue(verticalAlign, horizontalAlign, message, type) {
      this.$notify({
        component: NotificationTemplate,
        title: message,
        icon: "info",
        horizontalAlign: horizontalAlign,
        verticalAlign: verticalAlign,
        type: type,
        duration: 2000,
      });
    },
    showAddModal() {
      this.closeAllModals();
      this.showAddUserModal = true;
      document.body.classList.add('modal-open');
    },

    closeAddUserModal() {
      this.showAddUserModal = false;
      document.body.classList.remove('modal-open');
      this.newUser = {
        name: '',
        surname: '',
        mail: '',
        password: ''
      };
    },

    createUser() {
      this.isLoading = true;
      axios.post(`${config.app_url}/user/create-user`, this.newUser)
        .then(response => {
          if (response.data.success) {
            this.notifyVue("top", "right", this.$t('admin.users.createSuccess'), "success");
            this.closeAddUserModal();
            this.getUsers(); // Listeyi yenile
          } else {
            this.notifyVue("top", "right", response.data.msg, "warning");
          }
        })
        .catch(error => {
          this.notifyVue("top", "right", this.$t('admin.users.createError'), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },


    closeDeleteModal() {
      this.showDeleteModal = false;
      this.userToDelete = null;
    },

    confirmDelete() {
      if (!this.userToDelete) return;

      axios.delete(`${config.app_url}/user/delete-user/${this.userToDelete._id}`)
        .then(response => {
          if (response.data.success) {
            this.notifyVue("top", "right", this.$t('admin.users.deleteSuccess'), "success");
            this.getUsers(); // Listeyi yenile
          } else {
            this.notifyVue("top", "right", response.data.msg, "warning");
          }
        })
        .catch(error => {
          this.notifyVue("top", "right", this.$t('admin.users.deleteError'), "danger");
        })
        .finally(() => {
          this.closeDeleteModal();
        });
    },

    editUser(user) {
      this.closeAllModals();
      this.editingUser = {
        _id: user._id,
        name: user.name,
        surname: user.surname,
        mail: user.mail,
        password: '' // Şifreyi boş başlat
      };
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editingUser = {
        name: '',
        surname: '',
        mail: '',
        password: ''
      };
    },

    updateUser() {
      this.isLoading = true;

      // Eğer şifre boşsa, şifre alanını kaldır
      const updateData = { ...this.editingUser };
      if (!updateData.password) {
        delete updateData.password;
      }

      axios.put(`${config.app_url}/user/update-user-admin/?_id=${this.editingUser._id}`, updateData)
        .then(response => {
          if (response.data.success) {
            this.notifyVue("top", "right", this.$t('admin.users.updateSuccess'), "success");
            this.closeEditModal();
            this.getUsers(); // Listeyi yenile
          } else {
            this.notifyVue("top", "right", response.data.msg, "warning");
          }
        })
        .catch(error => {
          this.notifyVue("top", "right", this.$t('admin.users.updateError'), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getUserUrl(hash) {
      return this.basePreviewUrl + hash;
    },

    copyUserUrl(hash) {
      const url = this.getUserUrl(hash);
      navigator.clipboard.writeText(url)
        .then(() => {
          this.notifyVue(
            "top",
            "right",
            this.$t('admin.users.urlCopied'),
            "success"
          );
        })
        .catch(err => {
          console.error('URL kopyalama hatası:', err);
          this.notifyVue(
            "top",
            "right",
            this.$t('admin.users.urlCopyError'),
            "error"
          );
        });
    },
    closeAllModals() {
      this.showAddUserModal = false;
      this.showEditModal = false;
      this.showDeleteModal = false;
      document.body.classList.remove('modal-open');
    },
    formatLastLogin(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
}
</script>

<style scoped>
.table th {
  cursor: pointer;
  user-select: none;
}

.table th:hover {
  background-color: #f8f9fa;
}

.card {
  margin-bottom: 20px;
}

.form-control {
  width: 250px;
}
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.btn-outline-primary {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.btn-outline-primary:hover {
  color: #fff;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: none;
  z-index: 1040;
}

.modal-overlay.show {
  display: block;
}

.modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-wrapper.show {
  display: flex;
}

.modal-dialog {
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 1.75rem auto;
  background-color: #fff;
  border-radius: 0.3rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
  transform: translateY(-50px);
  opacity: 0;
  transition: all 0.3s ease-out;
}

.modal-wrapper.show .modal-dialog {
  transform: translateY(0);
  opacity: 1;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-body {
  padding: 1rem;
}

.modal-footer {
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.close {
  background: none;
  border: none;
  font-size: 1.5rem;
  padding: 0;
  cursor: pointer;
  font-weight: 700;
  line-height: 1;
  color: #000;
  text-shadow: 0 1px 0 #fff;
  opacity: .5;
}

.close:hover {
  opacity: .75;
}

/* Form stilleri */
.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
}

.form-control {
  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}
</style>
